/**
 * Recursos disponíveis no app web por portal — alimenta a tela "Recursos disponíveis" (menu Documentação).
 *
 * ATENÇÃO (17/10/2026): a fonte oficial agora é o ADM (Sistema > Portais e módulos > aba Disponibilidade, tabela
 * portais_recursos, com Web e Desktop), lida por GET api/Portais/RecursosDisponiveis. Este arquivo virou o RESERVA: só é
 * usado se a API não responder. Ao mudar o que o web libera de verdade, atualize também o ADM.
 *
 * Reserva: as colunas "Cadastro de proposta" e
 * "Resultado e documentação final" leem as MESMAS listas que o sistema usa para liberar a função,
 * então quando um portal sair de "em breve" (ex.: Licitanet, ComprasBR) a tela se atualiza sozinha.
 */
import { PORTAIS_CAD_PROPOSTA_WEB, PORTAIS_CAD_PROPOSTA_RESTRITOS } from '@/constants/portaisProposta';
import { PORTAIS_SYNC_FINAL, PORTAIS_SYNC_FINAL_RESTRITOS } from '@/api/sync_final_api';

// Estados de uma célula da tela
export const STATUS = { SIM: 'sim', BREVE: 'breve', NAO: 'nao' };

// Colunas da tela
export const recursos = [
    { key: 'busca',     titulo: 'Busca de editais',  desc: 'Pesquise licitações por palavra-chave, período e portal.' },
    { key: 'alertas',   titulo: 'Alertas',           desc: 'Avisos de novas oportunidades e mensagens dos pregões que você acompanha.' },
    { key: 'pregoes',   titulo: 'Meus pregões',      desc: 'Seus pregões, status e itens sincronizados automaticamente.' },
    { key: 'proposta',  titulo: 'Cadastro de proposta', desc: 'Cadastre e ajuste suas propostas direto pelo sistema.' },
    { key: 'lances',    titulo: 'Lances',            desc: 'Dispute em tempo real. Exige o módulo de lances web habilitado na sua conta.' },
    { key: 'resultado', titulo: 'Resultado e documentação final', desc: 'Vencedores, valores e geração dos documentos finais.' },
];

export const CHAVE_BB1 = '2-bb1';
export const CHAVE_BB2 = '2-bb2';

// Casos especiais do cadastro de proposta (validados ao vivo em 20/10/2026) e o que ainda fica no portal
export const notaCadastroCasosEspeciais =
    'Cadastro de proposta: o web trata maior desconto (ComprasNET e Licitanet, com o percentual em vez de R$), lote com vários itens '
    + '(Licitanet e PCP, avisando quando o lote está incompleto), leilão e registro de preços. No PCP, processos que exigem o '
    + 'comprovante do seguro garantia e credenciamento ainda são feitos direto no portal (o sistema avisa e abre o processo).';

export const notaLicitacoesE =
    'Licitações-e: o plano é o mesmo para as duas plataformas do Banco do Brasil, mas cada função é liberada por plataforma. '
    + 'No Licitações-e 2.0 você já pode buscar editais, e o cadastro de proposta está em testes (em breve para todos); alertas e '
    + 'acompanhamento ainda não estão disponíveis (o legado já tem tudo isso).';

// Recurso fora da matriz por portal (vale para a empresa, não para um portal): catálogo de produtos.
// Em teste: o menu "Minhas licitações > Produtos" só aparece para o CNPJ de teste (constants/produtos.js).
export const notaProdutos =
    'Produtos (em fase de testes): catálogo dos produtos que a sua empresa vende, com marca, modelo, CATMAT, preços, fornecedores e '
    + 'ficha técnica, para ser usado no cadastro de propostas. No cadastro de proposta (ComprasNET, Portal de Compras Públicas, '
    + 'BLL, BNC, LicitaNET, ComprasBR, Licitar Digital, Publinexo, BBMNET e Licitações-e legado) o botão "Produto" de cada item e o '
    + 'preenchimento rápido levam marca, modelo e o preço sugerido para a tela; no ComprasBR e no BBMNET a ficha técnica do '
    + 'produto também pode ir junto (no ComprasBR o arquivo só é enviado depois de você confirmar). A proposta só é gravada no '
    + 'portal quando você clica em Gravar. '
    + 'Os itens dos editais também ganham a coluna "Produto sugerido" (com o botão "Aplicar catálogo", que, por padrão, só '
    + 'preenche o que está vazio); a busca e os alertas de editais podem procurar "pelos meus produtos" (por palavras e pelo código CATMAT); '
    + 'e a tela "Oportunidades" lista os editais abertos que combinam com o seu catálogo (a procura roda todo dia, por volta '
    + 'das 08:30), com o botão "Preparar proposta" (que apenas deixa a tela pré-preenchida, sem gravar nada). '
    + 'Por enquanto está liberado só para a conta de testes.';

// Linhas da tela (ordem de exibição). `cor` é a cor do portal no cadastro (tabela portais).
export const portais = [
    { id: 1,  nome: 'ComprasNET',       cor: '#2C5CA6' },
    { id: 4,  nome: 'Portal de Compras Públicas', cor: '#00A8BB' },
    { id: 5,  nome: 'BLL Compras',      cor: '#177C5E' },
    { id: 8,  nome: 'BNC Compras',      cor: '#177C5E' },
    { id: 7,  nome: 'LicitaNET',        cor: '#FEC400' },
    { id: 9,  nome: 'ComprasBR',        cor: '#177EEE' },
    // O plano é o mesmo, mas são duas plataformas do BB com funções diferentes no web (a licitação traz qual é:
    // nomeportal 'Licitações-e' = BB1 legado, 'Licitações-e 2.0' = BB2). Chaves próprias para as duas linhas.
    { id: 2,  chave: CHAVE_BB1, nome: 'Licitações-e (BB1 — legado)', cor: '#FFF100' },
    { id: 2,  chave: CHAVE_BB2, nome: 'Licitações-e 2.0 (BB2)',       cor: '#FFF100' },
    { id: 10, nome: 'Licitar Digital',  cor: '#1AAE9E' },
    { id: 11, nome: 'AMM Licita',       cor: '#1CCE9F' },
    { id: 12, nome: 'Publinexo',        cor: '#10EE9F' },
    { id: 6,  nome: 'ComprasNET-BA',    cor: '#D2161E' },
    // Portais novos (só busca de editais): vêm do PNCP, sem cadastro de proposta, alertas, pregões nem lances.
    // Exceção: o BBMNET (13) já tem proposta e Meus pregões em homologação (aparecem como "Em breve").
    { id: 13, nome: 'BBMNET Licitações', cor: '#A63030' },
    { id: 14, nome: 'BR Conectado', cor: '#A65530' },
    { id: 15, nome: 'M2A Compras', cor: '#A67A30' },
    { id: 16, nome: 'Compras Rio de Janeiro', cor: '#A69F30' },
    { id: 17, nome: 'Licita+Brasil', cor: '#87A630' },
    { id: 18, nome: 'Compras Minas Gerais', cor: '#61A630' },
    { id: 19, nome: 'Compras Paraná (SEAP-PR)', cor: '#3CA630' },
    { id: 20, nome: 'Compras Rio Grande do Sul', cor: '#30A648' },
    { id: 21, nome: 'Pregão Banrisul', cor: '#30A66E' },
    { id: 22, nome: 'Compras Pernambuco', cor: '#30A693' },
    { id: 23, nome: 'Compras Amazonas', cor: '#3093A6' },
    { id: 24, nome: 'Compras Recife', cor: '#306EA6' },
    { id: 25, nome: 'Compras São José dos Campos', cor: '#3048A6' },
    { id: 26, nome: 'CENTI', cor: '#3C30A6' },
    { id: 27, nome: 'Compras Pará', cor: '#6130A6' },
    { id: 28, nome: 'Compras Santa Catarina', cor: '#8730A6' },
    { id: 29, nome: 'SISLOG Goiás', cor: '#A6309F' },
    { id: 30, nome: 'Central de Compras Paraíba', cor: '#A6307A' },
    { id: 31, nome: 'Compras Curitiba', cor: '#A63055' },
    // Sistemas de gestão municipal (ERPs) que publicam as compras dos municípios no PNCP: também só busca de editais.
    { id: 32, nome: 'IPM Sistemas', cor: '#A34D28' },
    { id: 33, nome: 'Megasoft', cor: '#A39F28' },
    { id: 34, nome: 'Governançabrasil', cor: '#55A328' },
    { id: 35, nome: 'Betha Sistemas', cor: '#28A34D' },
    { id: 36, nome: 'AZ Informática', cor: '#28A39F' },
    { id: 37, nome: 'Elotech', cor: '#2855A3' },
    { id: 38, nome: 'Abase Sistemas', cor: '#4D28A3' },
    { id: 39, nome: 'Tecnosweb', cor: '#9F28A3' },
    { id: 40, nome: 'CONAM', cor: '#A32855' },
];

// Portais com busca de editais: todos os listados (o BEC-SP está inativo e não aparece)
const comAlertas = [1, 4, 5, 8];          // alweb = 1 na tabela portais
const alertasEmBreve = [2, 7];            // Licitações-e legado (BB1) e Licitanet: alertas suspensos em 18/10/2026 até novo teste (BB2 não tem alertas)
const comPregoes = [1, 2, 4, 5, 7, 8, 9]; // sincronização de "Meus pregões" (2, 7 e 9 liberados em 18/10/2026)
const pregoesEmBreve = [13];              // BBMNET: sync de Meus pregões e cadastro de proposta em homologação (18/10/2026)
const lancesEmBreve = [1, 4, 5, 8];       // telas de lances web (menu Lances): prontas, mas ainda com ajustes e decisões pendentes
const resultadoEmBreve = [];              // resultado final: ver PORTAIS_SYNC_FINAL / PORTAIS_SYNC_FINAL_RESTRITOS

const em = (lista, id) => lista.includes(Number(id));

/** Estado (sim | breve | nao) de um recurso em um portal. */
export const statusRecurso = (recursoKey, idPortal) => {
    // Licitações-e 2.0 (BB2): busca de editais + cadastro de proposta (em teste desde 21/10/2026, só conta de testes);
    // alertas, pregões, lances e resultado ainda não existem no web
    if (idPortal === CHAVE_BB2) return recursoKey === 'busca' ? STATUS.SIM : recursoKey === 'proposta' ? STATUS.BREVE : STATUS.NAO;

    const id = Number(idPortal === CHAVE_BB1 ? 2 : idPortal);
    switch (recursoKey) {
        case 'busca':
            return STATUS.SIM;
        case 'alertas':
            return em(comAlertas, id) ? STATUS.SIM : em(alertasEmBreve, id) ? STATUS.BREVE : STATUS.NAO;
        case 'pregoes':
            return em(comPregoes, id) ? STATUS.SIM : em(pregoesEmBreve, id) ? STATUS.BREVE : STATUS.NAO;
        case 'proposta':
            return em(PORTAIS_CAD_PROPOSTA_WEB, id) ? STATUS.SIM
                : Object.prototype.hasOwnProperty.call(PORTAIS_CAD_PROPOSTA_RESTRITOS, id) ? STATUS.BREVE : STATUS.NAO;
        case 'lances':
            return em(lancesEmBreve, id) ? STATUS.BREVE : STATUS.NAO;
        case 'resultado':
            return em(PORTAIS_SYNC_FINAL, id) ? STATUS.SIM
                : (Object.prototype.hasOwnProperty.call(PORTAIS_SYNC_FINAL_RESTRITOS, id) || em(resultadoEmBreve, id)) ? STATUS.BREVE : STATUS.NAO;
        default:
            return STATUS.NAO;
    }
};

export const rotuloStatus = {
    [STATUS.SIM]: 'Disponível',
    [STATUS.BREVE]: 'Em breve',
    [STATUS.NAO]: 'Não disponível',
};
